import { useState, useEffect } from "react";
import { fetchTournamentLessonsAsync, type TournamentLesson } from "@/api/tournaments";
import { expandLanguageVariants } from "@/helpers/tournamentFilters";

export interface LessonFilters {
  language: string[];
  technology: string[];
  role: string[];
  level: string[];
  activityType: string[];
  release: string[];
}

export function useTournamentLessons(lessonFilters: LessonFilters): {
  tournamentLessons: TournamentLesson[];
  isLoading: boolean;
} {
  const [tournamentLessons, setTournamentLessons] = useState<TournamentLesson[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    const loadTournamentLessons = async () => {
      try {
        const lessons = await fetchTournamentLessonsAsync({
          tags: expandLanguageVariants([
            ...lessonFilters.language,
            ...lessonFilters.technology,
            ...lessonFilters.role,
          ]),
          difficulty: lessonFilters.level,
          lessonType: lessonFilters.activityType,
          release: lessonFilters.release,
        });
        if (!cancelled) setTournamentLessons(lessons);
      } catch (error) {
        console.error("Error fetching tournament lessons:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadTournamentLessons();
    return () => {
      cancelled = true;
    };
  }, [
    lessonFilters.language.join(","),
    lessonFilters.technology.join(","),
    lessonFilters.role.join(","),
    lessonFilters.level.join(","),
    lessonFilters.activityType.join(","),
    lessonFilters.release.join(","),
  ]);

  return { tournamentLessons, isLoading };
}
